import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/redux/store";
import { selectAllVisitors } from "@/redux/slices/visitorsSlice";
import { selectAllCalls } from "@/redux/slices/callsSlice";

interface SearchResult {
  id: string;
  type: "visitor" | "call";
  title: string;
  description?: string;
  action: () => void;
}

interface InlineSearchProps {
  onResultSelect?: () => void;
}

export default function InlineSearch({ onResultSelect }: InlineSearchProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const visitors = useAppSelector(selectAllVisitors);
  const calls = useAppSelector(selectAllCalls);

  const getSearchResults = (): SearchResult[] => {
    if (!query.trim()) return [];

    const results: SearchResult[] = [];
    const searchLower = query.toLowerCase();

    const safeToLower = (value: unknown): string => {
      if (value == null) return "";
      return String(value).toLowerCase();
    };

    const safeVisitors = Array.isArray(visitors) ? visitors : [];
    const safeCalls = Array.isArray(calls) ? calls : [];

    safeVisitors
      .filter((visitor) => {
        if (!visitor) return false;
        return (
          safeToLower(visitor.page_url).includes(searchLower) ||
          safeToLower(visitor.page_title).includes(searchLower) ||
          safeToLower(visitor.session_id).includes(searchLower)
        );
      })
      .slice(0, 5)
      .forEach((visitor) => {
        if (!visitor) return;
        results.push({
          id: `visitor-${visitor.id}`,
          type: "visitor",
          title: visitor.page_title || "Untitled Page",
          description: visitor.page_url || "",
          action: () => {
            navigate("/");
            onResultSelect?.();
          },
        });
      });

    safeCalls
      .filter((call) => {
        if (!call) return false;
        return (
          safeToLower(call.id).includes(searchLower) ||
          safeToLower(call.status).includes(searchLower)
        );
      })
      .slice(0, 5)
      .forEach((call) => {
        if (!call) return;
        try {
          const callType = call.call_type || "voice";
          const callTypeFormatted = callType.charAt(0).toUpperCase() + callType.slice(1);
          const status = call.status || "unknown";
          const dateStr = call.created_at
            ? new Date(call.created_at).toLocaleDateString()
            : "Unknown date";

          results.push({
            id: `call-${call.id}`,
            type: "call",
            title: `${callTypeFormatted} Call`,
            description: `Status: ${status} • ${dateStr}`,
            action: () => {
              navigate("/");
              onResultSelect?.();
            },
          });
        } catch {
          // Skip invalid call entries
        }
      });

    return results.slice(0, 10);
  };

  let results: SearchResult[] = [];
  try {
    results = getSearchResults();
  } catch {
    results = [];
  }

  const resultsRef = useRef(results);
  resultsRef.current = results;
  const showDropdown = isFocused && query.trim().length > 0;

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsFocused(false);
        inputRef.current?.blur();
      }
      if (e.ctrlKey && e.key === "/") {
        e.preventDefault();
        inputRef.current?.focus();
        setIsFocused(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Keyboard navigation when dropdown is open
  useEffect(() => {
    if (!showDropdown) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const res = resultsRef.current;
      if (res.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % res.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + res.length) % res.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = res[selectedIndex];
        if (item) {
          item.action();
          setQuery("");
          setIsFocused(false);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showDropdown, selectedIndex]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, Math.max(0, results.length - 1)));
  }, [results.length]);

  const handleResultClick = (result: SearchResult) => {
    result.action();
    setQuery("");
    setIsFocused(false);
  };

  return (
    <div ref={containerRef} className="relative flex-1">
      {/* Search input - dark theme, rounded, icon on right */}
      <div className="relative flex items-center w-full rounded-full bg-bg border border-transparent focus-within:border-border transition-colors">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder="Search visitors, calls..."
          className="w-full pl-4 pr-12 py-3 bg-transparent outline-none text-text placeholder:text-placeholder text-sm rounded-full"
          title="Search (Ctrl+/)"
        />
        <div className="absolute right-1 flex items-center justify-center size-9 rounded-full bg-light-2">
          <svg
            className="size-4 text-text"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <g id="style=linear">
                <g id="search-broken">
                  <path
                    id="vector"
                    d="M18.5 18.5L21 21"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  ></path>
                  <circle
                    id="vector_2"
                    cx="11.0529"
                    cy="11.0529"
                    r="8.3029"
                    stroke="currentColor"
                    stroke-width="1.5"
                  ></circle>
                </g>
              </g>
            </g>
          </svg>
        </div>
      </div>

      {/* Results dropdown with blur background */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 z-50 rounded-xl overflow-hidden shadow-xl border border-border bg-card/95 backdrop-blur-xl">
          <div className="max-h-[320px] sm-scroll py-2">
            {results.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-600 text-sm">
                No results found for &quot;{query}&quot;
              </div>
            ) : (
              <div className="py-1">
                {results.map((result, index) => (
                  <button
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                      index === selectedIndex
                        ? "bg-white/10 text-text"
                        : "hover:bg-white/5 text-text"
                    }`}
                  >
                    <div
                      className={`mt-1 shrink-0 ${
                        index === selectedIndex ? "text-pri" : "text-gray-400"
                      }`}
                    >
                      {result.type === "visitor" && (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      )}
                      {result.type === "call" && (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate text-text">{result.title}</div>
                      {result.description && (
                        <div className="text-sm truncate text-gray-400">{result.description}</div>
                      )}
                    </div>
                    <span className="px-2 py-1 text-xs rounded bg-white/10 text-gray-400 shrink-0">
                      {result.type}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
